import React from 'react';

const SUPABASE_STORAGE_BASE = process.env.REACT_APP_SUPABASE_STORAGE_URL;

const ResponsiveImage = ({
  src,
  alt = '',
  className = '',
  style = {},
  sizes,
  loading = 'lazy',
  fetchPriority = 'auto',
  objectFit = 'cover',
  objectPosition = 'center',
  onLoad,
  onError,
  useOptimized = true,
  ...props
}) => {
  const isSupabaseUrl = src && src.startsWith(SUPABASE_STORAGE_BASE);
  const isExternalUrl = src && src.startsWith('http') && !isSupabaseUrl;

  const getBaseName = (filename) => filename.replace(/\.(webp|jpg|jpeg|png)$/i, '');

  const generateSrcSet = () => {
    if (!useOptimized || !src) return undefined;

    const isFullWidth = sizes === '100vw';

    if (isSupabaseUrl) {
      // Extract filename from Supabase URL and build srcset from storage
      const filename = src.replace(SUPABASE_STORAGE_BASE, '');
      const baseName = getBaseName(filename);

      if (isFullWidth) {
        return [
          `${SUPABASE_STORAGE_BASE}${baseName}-medium.webp 1024w`,
          `${SUPABASE_STORAGE_BASE}${baseName}-large.webp 1920w`,
        ].join(', ');
      }

      return [
        `${SUPABASE_STORAGE_BASE}${baseName}-thumbnail.webp 400w`,
        `${SUPABASE_STORAGE_BASE}${baseName}-small.webp 640w`,
        `${SUPABASE_STORAGE_BASE}${baseName}-medium.webp 1024w`,
        `${SUPABASE_STORAGE_BASE}${baseName}-large.webp 1920w`,
      ].join(', ');
    }

    if (isExternalUrl) return undefined;

    // Local /assets-optimized/ srcset
    const baseName = getBaseName(src.replace('assets/', ''));
    const baseDir = '/assets-optimized/';

    if (isFullWidth) {
      return [
        `${baseDir}${baseName}-medium.webp 1024w`,
        `${baseDir}${baseName}-large.webp 1920w`,
      ].join(', ');
    }

    return [
      `${baseDir}${baseName}-thumbnail.webp 400w`,
      `${baseDir}${baseName}-small.webp 640w`,
      `${baseDir}${baseName}-medium.webp 1024w`,
      `${baseDir}${baseName}-large.webp 1920w`,
    ].join(', ');
  };

  const defaultSizes = sizes || '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw';

  const getFallbackSrc = () => {
    if (isExternalUrl) return src;

    if (isSupabaseUrl) {
      // Use medium as default for Supabase images
      const filename = src.replace(SUPABASE_STORAGE_BASE, '');
      const baseName = getBaseName(filename);
      return `${SUPABASE_STORAGE_BASE}${baseName}-medium.webp`;
    }

    // Local
    if (!useOptimized) return src.startsWith('/') ? src : `/${src}`;
    const baseName = getBaseName(src.replace('assets/', ''));
    return `/assets-optimized/${baseName}-medium.webp`;
  };

  const handleImageError = (e) => {
    const currentSrc = e.target.src;

    if (isSupabaseUrl && useOptimized) {
      const filename = src.replace(SUPABASE_STORAGE_BASE, '');
      const baseName = getBaseName(filename);

      if (currentSrc.includes('-medium.webp')) {
        e.target.src = `${SUPABASE_STORAGE_BASE}${baseName}-small.webp`;
      } else if (currentSrc.includes('-small.webp')) {
        e.target.src = `${SUPABASE_STORAGE_BASE}${baseName}-thumbnail.webp`;
      } else if (currentSrc.includes('-thumbnail.webp')) {
        // Last resort: original full-size from Supabase
        e.target.src = src;
        e.target.srcset = '';
      }
    } else if (!isExternalUrl && !isSupabaseUrl && useOptimized && currentSrc.includes('/assets-optimized/')) {
      const baseName = getBaseName(src.replace('assets/', ''));

      if (currentSrc.includes('-medium.webp')) {
        e.target.src = `/assets-optimized/${baseName}-small.webp`;
      } else if (currentSrc.includes('-small.webp')) {
        e.target.src = `/assets-optimized/${baseName}-thumbnail.webp`;
      } else if (currentSrc.includes('-thumbnail.webp')) {
        e.target.src = src.startsWith('/') ? src : `/assets/${src}`;
        e.target.srcset = '';
      }
    }

    if (onError) onError(e);
  };

  const srcSet = generateSrcSet();

  return (
    <img
      src={getFallbackSrc()}
      srcSet={srcSet}
      sizes={srcSet ? defaultSizes : undefined}
      alt={alt}
      className={className}
      style={{ objectFit, objectPosition, ...style }}
      loading={loading}
      fetchPriority={fetchPriority}
      onLoad={onLoad}
      onError={handleImageError}
      {...props}
    />
  );
};

export default ResponsiveImage;